/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Button from '@Button';
import Text from '@Text';
import ThankYouBanner from '@ThankYouBanner';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import brandLogos from '../../Assets/BrandLogos/brandLogos';
import {
  exposeThankYouPageReachedEvent,
  exposeUserActionEvent,
} from '../../Helpers/events';
import SelectedSupplierMatches from './SelectedSupplierMatches';
import { setSuccessAnimationColorToMatchTheme } from '../../Helpers/lottieAnimations';
import { updateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import Consent from '../Consent/Consent';
import SummaryContent from '../SummaryContent/SummaryContent';
import { VideoEmbed } from '../VideoEmbed';
import AppointmentBookingSection from './AppointmentBookingSection';
import LottieSuccessAnimation from './LottieSuccessAnimation';
import ThankYouSection from './ThankYouSection';
import useThankYouPageStyles from './makePageLayoutStyling';
import {
  logNoSubmissionIdError,
  shouldRenderContent,
} from './thankYouPage.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const ThankYouPage = () => {
  const { isDynamicHeightEnabled, widgetResizeDuration, iFrameId } =
    useSelector((store) => store.optionalFormConfig);
  const { showCpcRecommendations } = useSelector((store) => store.cpcCard);
  const { showAppointmentBooking, bookedAppointmentId, selectedAppointment } =
    useSelector((store) => store.appointmentBooking);
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const {
    brandLogo,
    thankYouSectionHeadline,
    thankYouSectionContent,
    referenceNumberText,
    whatHappensNextHeadline,
    whatHappensNextContent,
    additionalInfoHeadline,
    additionalInfoContent,
    thankYouSectionVideoEmbedUrl,
    thankYouPageDownloadCtaText,
    thankYouPageDownloadContentUrl,
    formId,
    showCustomerMatchesOnThankYouPage,
  } = useSelector((store) => store.formConfig);

  const { submissionSuccessAnimation, isRendererAvailable } = useSelector(
    (store) => store.lottieAnimationData
  );
  const submissionId = useSelector((store) => store.submission.id);
  const ancestorSubmissionId = useSelector(
    (store) => store.submission?.ancestorSid
  );

  const submissionIdForThankYouPage = ancestorSubmissionId || submissionId;

  const theme = useTheme();
  const lottieAnimationDataAndRendererReady =
    submissionSuccessAnimation && isRendererAvailable;
  const styles = useThankYouPageStyles(
    theme,
    lottieAnimationDataAndRendererReady
  );
  const BrandLogo = brandLogos[brandLogo];
  const dispatch = useDispatch();
  const thankYouPageContentRef = useRef();

  const [hasClickedDownloadContentCta, setHasClickedDownloadContentCta] =
    useState(false);

  const thankYouBannerExperimentName = useGetExperimentName({
    componentName: 'ThankYouBanner',
  });
  const buttonExperimentName = useGetExperimentName({
    componentName: 'Button',
  });
  const textExperimentName = useGetExperimentName({
    componentName: 'Text',
  });

  // To enable a variable widget height that dynamically changes across pages,
  // each page needs to expose its required height to fit all elements (including.
  // margin onto their page area)
  useEffect(() => {
    if (isDynamicHeightEnabled === true) {
      updateAndExposeDynamicHeightData({
        thankYouPageContentRef,
        newsletterSignupContainerRef: {},
      });
      const newRequiredTypHeight =
        thankYouPageContentRef.current?.getBoundingClientRect().height;
      const currentWidgetHeight = document.body.getBoundingClientRect().height;
      if (currentWidgetHeight !== newRequiredTypHeight) {
        dispatch(
          updateRequiredWidgetHeight({
            requiredWidgetHeight: newRequiredTypHeight,
            iFrameId,
            widgetResizeDuration,
            showCpcRecommendations,
            showAppointmentBooking,
            isBounceNavigationAnimationEnabled,
          })
        );
      }
    }
    if (lottieAnimationDataAndRendererReady) {
      setTimeout(() => setSuccessAnimationColorToMatchTheme(theme), 100);
    }
  }, []);

  useEffect(() => {
    if (!submissionIdForThankYouPage) {
      logNoSubmissionIdError(formId);
    } else {
      exposeThankYouPageReachedEvent({
        iFrameId,
        submissionId: submissionIdForThankYouPage,
        showCustomerMatchesOnThankYouPage,
      });
    }
  }, [submissionIdForThankYouPage]);

  const handleDownloadContentClick = () => {
    if (hasClickedDownloadContentCta === false) {
      exposeUserActionEvent({
        type: 'downloadContentClick',
        iFrameId,
        actionDetails: {
          text: thankYouPageDownloadCtaText,
          content: thankYouPageDownloadContentUrl,
        },
      });
    }

    setHasClickedDownloadContentCta(true);
    window.open(thankYouPageDownloadContentUrl, '_blank');
  };

  // i.e. if bookedAppointmentId is null it was an unsuccessful booking attempt, if it has a value it was successful, if no value then the booking has not been attempted
  const shouldShowAppointmentBooking =
    bookedAppointmentId || bookedAppointmentId === null;

  const failedToBookAppointment = bookedAppointmentId === null;

  const showLottieSuccessAnimation =
    !failedToBookAppointment && lottieAnimationDataAndRendererReady;

  return (
    <Grid
      container
      ref={thankYouPageContentRef}
      css={styles.submissionGridContainer}
      direction="column"
      data-testid="thankYouPage"
    >
      <Container
        css={styles.bannerContainer}
        maxWidth="md"
        data-testid="bannerContainer"
      >
        {!BrandLogo && <Box marginBottom="24px" />}
        <ThankYouBanner
          brandLogo={BrandLogo ? <BrandLogo /> : null}
          experimentName={thankYouBannerExperimentName}
          dataTestId="thankYouBanner"
        >
          {showLottieSuccessAnimation && (
            <LottieSuccessAnimation
              jsonAnimation={submissionSuccessAnimation}
              dataTestId="lottieSuccessAnimation"
            />
          )}
          {shouldShowAppointmentBooking ? (
            <AppointmentBookingSection
              referenceNumberText={referenceNumberText}
              bookedAppointmentId={bookedAppointmentId}
              submissionId={submissionIdForThankYouPage}
              selectedAppointment={selectedAppointment}
            />
          ) : (
            <ThankYouSection
              question={thankYouSectionHeadline}
              text={thankYouSectionContent}
              id={submissionIdForThankYouPage}
              idText={referenceNumberText}
            />
          )}
        </ThankYouBanner>
      </Container>
      {showCustomerMatchesOnThankYouPage && <SelectedSupplierMatches />}
      <Container
        css={styles.whatHappensNextSection}
        maxWidth="md"
        data-testid="submissionSummaryContainer"
      >
        <Consent />
        {shouldRenderContent(whatHappensNextHeadline) && (
          <Box
            marginTop={theme.mvf.spacing.medium}
            data-testid="whatHappensNextHeadline"
          >
            <Text
              variant="header"
              align="left"
              experimentName={textExperimentName}
            >
              {whatHappensNextHeadline}
            </Text>
          </Box>
        )}
        {thankYouSectionVideoEmbedUrl && (
          <VideoEmbed videoUrl={thankYouSectionVideoEmbedUrl} />
        )}
        {shouldRenderContent(whatHappensNextContent) && (
          <Box marginBottom="36px" data-testid="whatHappensNextContent">
            <Text variant="summary" experimentName={textExperimentName}>
              <Box>
                <SummaryContent spacing={theme.mvf.spacing.medium}>
                  {whatHappensNextContent}
                </SummaryContent>
              </Box>
            </Text>
          </Box>
        )}
        {shouldRenderContent(additionalInfoHeadline) && (
          <Box
            marginBottom={theme.mvf.spacing.tiny}
            data-testid="additionalInfoHeadline"
          >
            <Text
              variant="header"
              align="left"
              experimentName={textExperimentName}
            >
              {additionalInfoHeadline}
            </Text>
          </Box>
        )}
        {shouldRenderContent(additionalInfoContent) && (
          <Box data-testid="additionalInfoContent">
            <Text variant="summary" experimentName={textExperimentName}>
              <SummaryContent spacing={theme.mvf.spacing.medium}>
                {additionalInfoContent}
              </SummaryContent>
            </Text>
          </Box>
        )}
      </Container>

      {thankYouPageDownloadCtaText && thankYouPageDownloadContentUrl && (
        <Container css={styles.viewWhitepaperSection} maxWidth="md">
          <Box
            css={styles.thankYouPageContentDownloadContainer}
            data-testid="thankYouPageContentDownloadContainer"
          >
            <Button
              label={thankYouPageDownloadCtaText}
              onClick={handleDownloadContentClick}
              dataTestId="download-content-cta"
              experimentName={buttonExperimentName}
            />
          </Box>
        </Container>
      )}
    </Grid>
  );
};

export default ThankYouPage;
